import { HOST_API_URL } from '../../config-global';

export function getInvoiceFileExtension(fileName = '', fileExtension = '') {
  if (fileExtension) return String(fileExtension).toLowerCase();
  const splitName = String(fileName).split('.');
  if (splitName.length < 2) return '';
  return splitName.pop().toLowerCase();
}

export function isPreviewableInvoiceFile({ fileName = '', fileExtension = '' }) {
  const extension = getInvoiceFileExtension(fileName, fileExtension);
  return extension === 'pdf' || extension === 'html';
}

export function parseInvoiceFileMetaFromUrl(url) {
  if (!url) return null;
  try {
    const normalizedUrl = new URL(url, window.location.origin);
    const idParam = normalizedUrl.searchParams.get('id');
    if (!idParam) return null;
    const mainFileParam = normalizedUrl.searchParams.get('mainFile');
    return {
      id: Number(idParam),
      mainFile: String(mainFileParam).toLowerCase() === 'true',
    };
  } catch {
    return null;
  }
}

export async function openInvoiceFileInNewTab({
  id,
  mainFile = false,
  fileName = '',
  fileExtension = '',
  downloadInvoiceFileApi,
}) {
  if (!isPreviewableInvoiceFile({ fileName, fileExtension })) return false;
  if (!downloadInvoiceFileApi) return false;

  const response = await downloadInvoiceFileApi(id, mainFile);
  const extension = getInvoiceFileExtension(fileName, fileExtension);
  const blobType = extension === 'html' ? 'text/html' : 'application/pdf';
  const blob = new Blob([response.data], { type: blobType });
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return true;
}

export function downloadInvoiceFileInNewTab({ id, mainFile = false }) {
  if (!id) return;
  window.open(`${HOST_API_URL}Api/Invoices/DownloadInvoiceFile?id=${id}&mainFile=${mainFile}`, '_blank');
}

export async function runInvoiceAttachmentHrefAction({
  href,
  fileName = '',
  action = 'open',
  downloadInvoiceFileApi,
}) {
  const fileMeta = parseInvoiceFileMetaFromUrl(href);
  if (!fileMeta) {
    if (href) window.open(href, '_blank');
    return;
  }

  if (action === 'download') {
    downloadInvoiceFileInNewTab({
      id: fileMeta.id,
      mainFile: fileMeta.mainFile,
    });
    return;
  }

  await openInvoiceFileInNewTab({
    id: fileMeta.id,
    mainFile: fileMeta.mainFile,
    fileName,
    downloadInvoiceFileApi,
  });
}

export function splitInvoiceMessageContentAndAttachments(note = '', invoiceFiles = []) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(note || '', 'text/html');
  const attachmentLinks = [];
  const invoiceFileNameById = new Map(
    (invoiceFiles || [])
      .filter((file) => Number.isFinite(Number(file?.id)))
      .map((file) => {
        const extension = getInvoiceFileExtension(file?.fileName, file?.fileExtension);
        const hasExtension = extension && String(file?.fileName || '').toLowerCase().endsWith(`.${extension}`);
        const resolvedName = hasExtension || !extension
          ? String(file?.fileName || '').trim()
          : `${String(file?.fileName || '').trim()}.${extension}`;
        return [Number(file.id), resolvedName];
      })
  );

  const attachments = doc.querySelectorAll('a.attachment');
  attachments.forEach((anchor) => {
    const attachmentIdAttr = anchor.getAttribute('data-file-id');
    const parsedAttachmentId = /^\d+$/.test(String(attachmentIdAttr || '').trim())
      ? Number(attachmentIdAttr)
      : null;

    if (!Number.isFinite(parsedAttachmentId)) {
      anchor.remove();
      return;
    }

    const href = `${HOST_API_URL}Api/Invoices/DownloadInvoiceFile?id=${parsedAttachmentId}&mainFile=false`;

    const anchorText = (anchor.textContent || '').trim();
    const resolvedFileName =
      (Number.isFinite(parsedAttachmentId) && invoiceFileNameById.get(parsedAttachmentId)) ||
      anchorText ||
      'Attachment';

    attachmentLinks.push({
      href,
      attachmentId: parsedAttachmentId,
      fileName: resolvedFileName,
    });
    anchor.remove();
  });

  return {
    messageContent: doc.body.innerHTML,
    attachmentLinks,
  };
}

